import { useEffect, useRef } from 'react'

interface FlashcardWithAudio {
  audioUrl: string | null
  [key: string]: any // Allow other flashcard properties
}

/**
 * Hook to preload audio for upcoming flashcards to ensure instant playback.
 * Preloads the next 2 flashcards' audio in the background and manages memory efficiently.
 *
 * @param flashcards - Array of flashcards with potential audio URLs
 * @param currentIndex - Current flashcard index being reviewed
 * @param preloadCount - Number of flashcards to preload ahead (default: 2)
 */
export function useFlashcardAudioPreloader(
  flashcards: FlashcardWithAudio[],
  currentIndex: number,
  preloadCount: number = 2
) {
  // Cache of preloaded Audio instances
  const audioCache = useRef<Map<number, HTMLAudioElement>>(new Map())

  // Track loading status for debugging
  const loadingStatus = useRef<Map<number, 'loading' | 'loaded' | 'error'>>(new Map())

  useEffect(() => {
    // Determine which flashcards to preload
    const preloadIndices = Array.from(
      { length: preloadCount },
      (_, i) => currentIndex + i + 1
    ).filter((index) => index < flashcards.length)

    // Preload upcoming flashcards
    preloadIndices.forEach((index) => {
      // Skip if already cached
      if (audioCache.current.has(index)) {
        return
      }

      const flashcard = flashcards[index]
      if (flashcard?.audioUrl) {
        // Create new Audio instance for preloading
        const audio = new Audio(flashcard.audioUrl)
        audio.preload = 'auto'

        // Set loading status
        loadingStatus.current.set(index, 'loading')

        // Handle successful load
        audio.addEventListener('canplaythrough', () => {
          loadingStatus.current.set(index, 'loaded')
          console.log(`[AudioPreloader] Preloaded flashcard ${index}`)
        }, { once: true })

        // Handle loading errors
        audio.addEventListener('error', (e) => {
          loadingStatus.current.set(index, 'error')
          console.error(`[AudioPreloader] Failed to preload flashcard ${index}:`, e)
        }, { once: true })

        // Start loading
        audio.load()

        // Store in cache
        audioCache.current.set(index, audio)
      }
    })

    // Clean up old cached audios to manage memory
    const validIndices = new Set([...preloadIndices, currentIndex])
    audioCache.current.forEach((audio, index) => {
      // Keep current and upcoming flashcards in cache
      if (!validIndices.has(index) && index < currentIndex) {
        // Cleanup audio instance
        audio.pause()
        audio.src = ''
        audio.load() // Reset the element

        // Remove from cache
        audioCache.current.delete(index)
        loadingStatus.current.delete(index)

        console.log(`[AudioPreloader] Cleaned up flashcard ${index}`)
      }
    })
  }, [currentIndex, flashcards, preloadCount])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      audioCache.current.forEach((audio) => {
        audio.pause()
        audio.src = ''
        audio.load()
      })
      audioCache.current.clear()
      loadingStatus.current.clear()
      console.log('[AudioPreloader] Cleaned up all cached audio')
    }
  }, [])

  // Return helper functions if needed by the consumer
  return {
    /**
     * Get a preloaded audio element if available
     */
    getPreloadedAudio: (index: number): HTMLAudioElement | undefined => {
      return audioCache.current.get(index)
    },

    /**
     * Check if a specific flashcard's audio is preloaded
     */
    isPreloaded: (index: number): boolean => {
      return loadingStatus.current.get(index) === 'loaded'
    },

    /**
     * Get loading status for a specific flashcard
     */
    getLoadingStatus: (index: number): 'loading' | 'loaded' | 'error' | 'not-started' => {
      return loadingStatus.current.get(index) || 'not-started'
    },

    /**
     * Manually trigger preload for a specific index
     */
    preloadIndex: (index: number) => {
      if (index >= 0 && index < flashcards.length && !audioCache.current.has(index)) {
        const flashcard = flashcards[index]
        if (flashcard?.audioUrl) {
          const audio = new Audio(flashcard.audioUrl)
          audio.preload = 'auto'
          audio.load()
          audioCache.current.set(index, audio)
        }
      }
    },
  }
}